package com.trevorwiebe.timesheet.admin.employeeHours.presentation

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.VerticalDivider
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.trevorwiebe.timesheet.admin.employeeHours.domain.EmployeeHoursSummary
import com.trevorwiebe.timesheet.admin.employeeHours.domain.PeriodSummary
import com.trevorwiebe.timesheet.admin.employeeHours.domain.toPreviousPayPeriodCsv
import com.trevorwiebe.timesheet.core.presentation.TopBar
import com.trevorwiebe.timesheet.core.presentation.common.BackIcon
import com.trevorwiebe.timesheet.core.presentation.common.FileExportIcon
import com.trevorwiebe.timesheet.core.presentation.common.NativeDialog
import com.trevorwiebe.timesheet.core.presentation.common.ResponsiveContent
import com.trevorwiebe.timesheet.core.presentation.common.botPadding
import com.trevorwiebe.timesheet.core.presentation.common.rememberCsvExporter
import com.trevorwiebe.timesheet.payPeriod.presentation.uiUtils.ChipItem
import com.trevorwiebe.timesheet.payPeriod.presentation.uiUtils.color
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun EmployeeHoursScreen(
    onEmployeeClick: (userId: String, employeeName: String) -> Unit,
    onBack: () -> Unit,
    viewModel: EmployeeHoursViewModel = koinViewModel(),
) {
    val state by viewModel.state.collectAsState()
    val exportCsv = rememberCsvExporter()

    // Re-runs each time the screen re-enters composition (including back-navigation), while the
    // ViewModel is retained — so confirmations made on the drill-down screens show up here.
    LaunchedEffect(Unit) { viewModel.refresh() }

    var showConfirmExport by remember { mutableStateOf(false) }
    var showCannotExport by remember { mutableStateOf(false) }

    val canTapExport = !state.isLoading && state.summaries.isNotEmpty()

    Scaffold(
        topBar = {
            TopBar(
                title = "Employee Hour Reports",
                backIcon = { BackIcon() },
                onBack = onBack,
                actions = {
                    IconButton(
                        onClick = {
                            if (state.canExportPrevious) showConfirmExport = true
                            else showCannotExport = true
                        },
                        enabled = canTapExport
                    ) {
                        Icon(
                            imageVector = FileExportIcon,
                            contentDescription = "Export previous pay period",
                            tint = if (canTapExport) {
                                MaterialTheme.colorScheme.tertiary
                            } else {
                                MaterialTheme.colorScheme.onSurface.copy(alpha = 0.38f)
                            }
                        )
                    }
                }
            )
        }
    ) { paddingValues ->
        ResponsiveContent(modifier = Modifier.padding(paddingValues)) {
        when {
            state.isLoading -> {
                Box(
                    modifier = Modifier.fillMaxSize(),
                    contentAlignment = Alignment.Center
                ) {
                    CircularProgressIndicator(color = MaterialTheme.colorScheme.tertiary)
                }
            }

            state.error != null -> {
                Box(
                    modifier = Modifier.fillMaxSize().padding(16.dp),
                    contentAlignment = Alignment.Center
                ) {
                    Text(
                        text = state.error ?: "Something went wrong",
                        color = MaterialTheme.colorScheme.error
                    )
                }
            }

            else -> {
                Column(modifier = Modifier.fillMaxSize()) {
                    if (state.isRefreshing) {
                        LinearProgressIndicator(
                            modifier = Modifier.fillMaxWidth(),
                            color = MaterialTheme.colorScheme.tertiary,
                        )
                    }
                    LazyColumn(modifier = Modifier.fillMaxSize()) {
                        items(state.summaries) { summary ->
                            EmployeeHoursCard(summary, onEmployeeClick)
                        }
                        item {
                            Spacer(Modifier.height(botPadding()))
                        }
                    }
                }
            }
        }
        }
    }

    NativeDialog(
        visible = showConfirmExport,
        title = "Export previous pay period",
        message = "This submits the previous pay period to payroll. Employees will no longer be " +
            "able to edit it. Continue?",
        confirmText = "Export",
        onConfirm = {
            showConfirmExport = false
            val csv = state.summaries.toPreviousPayPeriodCsv(state.rates)
            exportCsv(csv.fileName, csv.content)
            viewModel.markPreviousPeriodSubmitted()
        },
        dismissText = "Cancel",
        onDismiss = { showConfirmExport = false },
    )

    NativeDialog(
        visible = showCannotExport,
        title = "Cannot export yet",
        message = "All employees must confirm their previous pay period before it can be exported.",
        confirmText = "OK",
        onConfirm = { showCannotExport = false },
        dismissText = "Cancel",
        onDismiss = { showCannotExport = false },
    )
}

@Composable
private fun EmployeeHoursCard(
    summary: EmployeeHoursSummary,
    onEmployeeClick: (userId: String, employeeName: String) -> Unit,
) {
    Card(
        modifier = Modifier.fillMaxWidth().padding(8.dp),
        colors = CardDefaults.cardColors(
            containerColor = MaterialTheme.colorScheme.surface
        )
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .clickable { onEmployeeClick(summary.employee.id, summary.employee.displayName) }
                .padding(12.dp)
        ) {
            Text(
                text = summary.employee.displayName,
                fontWeight = FontWeight.Bold,
                fontSize = 18.sp,
                color = MaterialTheme.colorScheme.secondary
            )
            Spacer(Modifier.height(8.dp))
            Row(modifier = Modifier.fillMaxWidth()) {
                PeriodBlock(
                    modifier = Modifier.weight(1f),
                    title = "Current Pay Period",
                    period = summary.current,
                )
                VerticalDivider(modifier = Modifier.padding(horizontal = 8.dp))
                PeriodBlock(
                    modifier = Modifier.weight(1f),
                    title = "Previous Pay Period",
                    period = summary.previous,
                )
            }
        }
    }
}

@Composable
private fun PeriodBlock(
    modifier: Modifier = Modifier,
    title: String,
    period: PeriodSummary,
) {
    Column(modifier = modifier) {
        Text(
            text = title,
            fontWeight = FontWeight.SemiBold,
            fontSize = 15.sp
        )
        Spacer(Modifier.height(4.dp))

        if (period.hoursByRate.isEmpty()) {
            Text(
                text = "No hours",
                fontSize = 14.sp,
                color = MaterialTheme.colorScheme.secondary
            )
        } else {
            period.hoursByRate.forEach { (rate, hours) ->
                Text(text = "$rate: $hours", fontSize = 14.sp)
            }
        }

        if (period.status.isNotEmpty()) {
            Spacer(Modifier.height(6.dp))
            Column {
                period.status.forEach { status ->
                    Row {
                        ChipItem(status.title, status.color)
                    }
                    Spacer(Modifier.height(4.dp))
                }
            }
        }
    }
}
